import { Box, Popover, Typography, IconButton, styled } from '@mui/material'
import { FC, useState, useRef } from 'react'
import BasicDatePicker from 'src/components/DateTimePicker'
import { MainComponentFilters } from 'src/generated-types/main-component'
import { useTranslation } from 'react-i18next'
import { CalendarMonth } from '@mui/icons-material'
import { Flex } from 'src/styles/flexComponent'
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'

interface DateFilterProps {
  field: string
  headerFilters: MainComponentFilters
  updateFilter: (field: string, value: string) => void
  mode?: 'yearMonth' | 'fullDate'
}

const DateFilter: FC<DateFilterProps> = ({
  field,
  headerFilters,
  updateFilter,
  mode = 'yearMonth',
}) => {
  const { t: translate } = useTranslation(['common'])
  const [showPopover, setShowPopover] = useState(false)
  const [selectedYear, setSelectedYear] = useState<number | null>(null)
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null)
  const currentViewRef = useRef<'year' | 'month' | 'day'>('year')
  const anchorRef = useRef<HTMLDivElement>(null)

  const currentFilterValue = headerFilters[field as keyof MainComponentFilters] as string

  const handleIconClick = (e: React.MouseEvent) => {
    e.stopPropagation()

    if (mode === 'yearMonth') {
      if (currentFilterValue) {
        if (currentFilterValue.length === 4) {
          setSelectedYear(parseInt(currentFilterValue))
          setSelectedMonth(null)
        } else {
          const [year, month] = currentFilterValue.split('-')
          setSelectedYear(parseInt(year))
          setSelectedMonth(parseInt(month))
        }
      } else {
        setSelectedYear(null)
        setSelectedMonth(null)
      }
    }

    if (mode === 'fullDate') {
      currentViewRef.current = 'day'
    }

    setShowPopover(true)
  }

  const handleYearChange = (newValue: Date | null) => {
    if (newValue) {
      const year = newValue.getFullYear()
      setSelectedYear(year)

      if (selectedMonth) {
        const monthString = String(selectedMonth).padStart(2, '0')
        updateFilter(field, `${year}-${monthString}`)
      } else {
        updateFilter(field, `${year}`)
      }
    }
  }

  const handleMonthChange = (newValue: Date | null) => {
    if (newValue && selectedYear) {
      const month = newValue.getMonth() + 1
      setSelectedMonth(month)

      const monthString = String(month).padStart(2, '0')
      updateFilter(field, `${selectedYear}-${monthString}`)
    }
  }

  const handleStaticFullDateChange = (newValue: Date | null) => {
    if (newValue && currentViewRef.current === 'day') {
      const year = newValue.getFullYear()
      const month = String(newValue.getMonth() + 1).padStart(2, '0')
      const day = String(newValue.getDate()).padStart(2, '0')
      updateFilter(field, `${year}-${month}-${day}`)
      setShowPopover(false)
    }
  }

  const handleClose = () => {
    setShowPopover(false)
  }

  const fullDateValue = mode === 'fullDate' ? (currentFilterValue ? new Date(currentFilterValue) : null) : null

  return (
    <StyledBox ref={anchorRef}>
      <IconButton size="small" onClick={handleIconClick} sx={{ padding: '4px' }}>
        <CalendarMonth sx={{ fontSize: '25px' }} />
      </IconButton>

      <Popover
        open={showPopover}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {mode === 'fullDate' ? (
          <Box>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <StaticDatePicker
                displayStaticWrapperAs="desktop"
                views={['year', 'month', 'day']}
                openTo="day"
                value={fullDateValue}
                onChange={handleStaticFullDateChange}
                onViewChange={(view: 'year' | 'month' | 'day') => { currentViewRef.current = view }}
                renderInput={() => <></>}
              />
            </LocalizationProvider>
          </Box>
        ) : (
          <Flex.Row gap={1} padding={2}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ paddingLeft: 1 }}>
                {translate('productionYear', { ns: 'main-component' })}
              </Typography>
              <BasicDatePicker
                name={`filter-year-${field}`}
                views={['year']}
                inputFormat={'yyyy'}
                openTo={'year'}
                value={selectedYear ? new Date(selectedYear, 0, 1) : null}
                onChange={handleYearChange}
                isWithoutAppForm={true}
              />
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ paddingLeft: 1 }}>
                {translate('productionMonth', { ns: 'main-component' })}
              </Typography>
              <BasicDatePicker
                name={`filter-month-${field}`}
                views={['month']}
                inputFormat={'MMMM'}
                openTo={'month'}
                value={selectedMonth && selectedYear ? new Date(selectedYear, selectedMonth - 1, 1) : null}
                onChange={handleMonthChange}
                disabled={!selectedYear}
                isWithoutAppForm={true}
              />
            </Box>
          </Flex.Row>
        )}
      </Popover>
    </StyledBox>
  )
}

const StyledBox = styled(Box)(() => ({
  height: '30px',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0.5,
}))

export default DateFilter